import React, { useState, useMemo } from 'react';
import { 
  Shield, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  AlertTriangle,
  CheckCircle, 
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  User,
  Building,
  CreditCard,
  FileText,
  Activity,
  ArrowLeft,
  BarChart3,
  PieChart
} from 'lucide-react';

// Mock credit check data
const creditChecks = [
  {
    id: 'credit-001',
    check_number: 'CC-2025-001',
    customer_id: 'cust-001',
    customer_name: '台灣電子科技股份有限公司',
    customer_code: 'TETC-001',
    request_date: '2025-01-15',
    completion_date: '2025-01-16',
    requested_by: '業務經理_張三',
    status: 'completed',
    credit_limit: 20000000,
    recommended_limit: 15000000,
    risk_level: 'medium',
    credit_score: 750,
    payment_terms: '30天',
    financial_data: {
      annual_revenue: 500000000,
      net_income: 50000000,
      total_assets: 300000000,
      total_liabilities: 150000000,
      current_ratio: 2.1,
      debt_to_equity: 0.5,
      quick_ratio: 1.8
    },
    payment_history: {
      total_transactions: 45,
      on_time_payments: 42,
      late_payments: 3,
      average_payment_days: 28,
      longest_overdue: 7
    },
    risk_factors: [
      {
        factor: '行業風險',
        level: 'low',
        description: '電子科技業穩定成長'
      },
      {
        factor: '財務狀況',
        level: 'medium',
        description: '現金流良好但負債比例稍高'
      },
      {
        factor: '付款記錄',
        level: 'low',
        description: '付款記錄良好，偶有延遲'
      }
    ],
    recommendations: [
      '建議信用額度設定為1,500萬元',
      '付款條件維持30天',
      '建議每季度重新評估',
      '可考慮要求銀行保證'
    ]
  },
  {
    id: 'credit-002',
    check_number: 'CC-2025-002',
    customer_id: 'cust-002',
    customer_name: '創新科技股份有限公司',
    customer_code: 'INNOV-002',
    request_date: '2025-01-10',
    completion_date: null,
    requested_by: '業務經理_李四',
    status: 'pending',
    credit_limit: 10000000,
    recommended_limit: null,
    risk_level: 'high',
    credit_score: 620,
    payment_terms: '45天',
    financial_data: {
      annual_revenue: 200000000,
      net_income: 15000000,
      total_assets: 120000000,
      total_liabilities: 90000000,
      current_ratio: 1.3,
      debt_to_equity: 3.0,
      quick_ratio: 0.9
    },
    payment_history: {
      total_transactions: 12,
      on_time_payments: 8,
      late_payments: 4,
      average_payment_days: 52,
      longest_overdue: 21
    },
    risk_factors: [
      {
        factor: '行業風險',
        level: 'medium',
        description: '新興科技業波動較大'
      },
      {
        factor: '財務狀況',
        level: 'high',
        description: '負債比例過高，流動性不足'
      },
      {
        factor: '付款記錄',
        level: 'high',
        description: '經常延遲付款，最長逾期21天'
      }
    ],
    recommendations: [
      '建議降低信用額度至500萬元',
      '要求現金擔保或銀行保證',
      '縮短付款條件至15天',
      '每月監控付款狀況'
    ]
  }
];

const CreditCheck = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getRiskColor = (level) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getRiskLabel = (level) => {
    const labels = {
      'low': '低風險',
      'medium': '中風險',
      'high': '高風險'
    };
    return labels[level] || level;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'reviewing': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '待審核',
      'completed': '已完成',
      'rejected': '已拒絕',
      'reviewing': '審核中'
    };
    return labels[status] || status;
  };

  const getCreditScoreColor = (score) => {
    if (score >= 750) return 'text-green-600';
    if (score >= 650) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredChecks = useMemo(() => {
    return creditChecks.filter(check => {
      if (statusFilter !== 'all' && check.status !== statusFilter) {
        return false;
      }
      if (riskFilter !== 'all' && check.risk_level !== riskFilter) {
        return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          check.check_number.toLowerCase().includes(searchLower) ||
          check.customer_name.toLowerCase().includes(searchLower) ||
          check.customer_code.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [statusFilter, riskFilter, searchTerm]);

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-purple-600">階段C: 商務談判與銷售訂單確認 - 信用檢查</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => {
    const totalChecks = creditChecks.length;
    const completedChecks = creditChecks.filter(c => c.status === 'completed').length;
    const pendingChecks = creditChecks.filter(c => c.status === 'pending').length;
    const highRiskChecks = creditChecks.filter(c => c.risk_level === 'high').length;
    const averageScore = creditChecks.reduce((sum, c) => sum + c.credit_score, 0) / creditChecks.length;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">信用檢查儀表板</h2>
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增信用檢查</span>
          </button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">總檢查數</p>
                <p className="text-2xl font-bold text-gray-800">{totalChecks}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">已完成</p>
                <p className="text-2xl font-bold text-gray-800">{completedChecks}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">待審核</p>
                <p className="text-2xl font-bold text-gray-800">{pendingChecks}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">高風險客戶</p>
                <p className="text-2xl font-bold text-gray-800">{highRiskChecks}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 信用評分分佈 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">信用評分分佈</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">優良 (750+)</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">良好 (650-749)</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">一般 (550-649)</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
            </div>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">平均信用評分</span>
                <span className={`text-lg font-bold ${getCreditScoreColor(averageScore)}`}>
                  {Math.round(averageScore)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">風險等級分佈</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">低風險</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">中風險</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <span className="text-sm font-medium">0</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">高風險</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div className="bg-red-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-sm font-medium">1</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 最近的信用檢查 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">最近的信用檢查</h3>
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                查看全部
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {creditChecks.map((check) => (
                <div
                  key={check.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedCheck(check);
                    setActiveView('detail');
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{check.customer_name}</h4>
                      <p className="text-sm text-gray-600">{check.check_number} - 信用評分: {check.credit_score}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">建議額度: {check.recommended_limit ? formatCurrency(check.recommended_limit) : '待定'}</p>
                      <p className="text-sm text-gray-600">申請日期: {check.request_date}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(check.risk_level)}`}>
                      {getRiskLabel(check.risk_level)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(check.status)}`}>
                      {getStatusLabel(check.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">信用檢查列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增信用檢查</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋客戶或檢查編號..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="pending">待審核</option>
            <option value="completed">已完成</option>
            <option value="rejected">已拒絕</option>
            <option value="reviewing">審核中</option>
          </select>
          
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有風險等級</option>
            <option value="low">低風險</option>
            <option value="medium">中風險</option>
            <option value="high">高風險</option>
          </select>
        </div>
      </div>

      {/* 信用檢查表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                信用評分
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                風險等級
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                建議額度
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                狀態
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredChecks.map((check) => (
              <tr key={check.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{check.customer_name}</div>
                      <div className="text-sm text-gray-500">{check.check_number}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-medium ${getCreditScoreColor(check.credit_score)}`}>
                    {check.credit_score}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(check.risk_level)}`}>
                    {getRiskLabel(check.risk_level)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {check.recommended_limit ? formatCurrency(check.recommended_limit) : '待定'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(check.status)}`}>
                    {getStatusLabel(check.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedCheck(check);
                      setActiveView('detail');
                    }}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedCheck) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">信用檢查詳細資訊</h2>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回列表</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">基本資訊</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">檢查編號:</span>
                <span className="font-medium">{selectedCheck.check_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">客戶名稱:</span>
                <span className="font-medium">{selectedCheck.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">申請日期:</span>
                <span className="font-medium">{selectedCheck.request_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">完成日期:</span>
                <span className="font-medium">{selectedCheck.completion_date || '待完成'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">申請人:</span>
                <span className="font-medium">{selectedCheck.requested_by}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">狀態:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCheck.status)}`}>
                  {getStatusLabel(selectedCheck.status)}
                </span>
              </div>
            </div>
          </div>

          {/* 信用評估 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">信用評估</h3>
            <div className="space-y-4">
              <div className="text-center">
                <div className={`text-4xl font-bold ${getCreditScoreColor(selectedCheck.credit_score)}`}>
                  {selectedCheck.credit_score}
                </div>
                <p className="text-sm text-gray-600">信用評分</p>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">風險等級:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(selectedCheck.risk_level)}`}>
                  {getRiskLabel(selectedCheck.risk_level)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">申請額度:</span>
                <span className="font-medium">{formatCurrency(selectedCheck.credit_limit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">建議額度:</span>
                <span className="font-medium">{selectedCheck.recommended_limit ? formatCurrency(selectedCheck.recommended_limit) : '待定'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">付款條件:</span>
                <span className="font-medium">{selectedCheck.payment_terms}</span>
              </div>
            </div>
          </div>

          {/* 付款記錄 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">付款記錄</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">交易總數:</span>
                <span className="font-medium">{selectedCheck.payment_history.total_transactions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">準時付款:</span>
                <span className="font-medium text-green-600">{selectedCheck.payment_history.on_time_payments}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">延遲付款:</span>
                <span className="font-medium text-red-600">{selectedCheck.payment_history.late_payments}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">平均付款天數:</span>
                <span className="font-medium">{selectedCheck.payment_history.average_payment_days}天</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">最長逾期:</span>
                <span className="font-medium">{selectedCheck.payment_history.longest_overdue}天</span>
              </div>
            </div>
          </div>
        </div>

        {/* 財務資料 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">財務資料</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">年營收</p>
              <p className="text-lg font-semibold">{formatCurrency(selectedCheck.financial_data.annual_revenue)}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">淨利潤</p>
              <p className="text-lg font-semibold">{formatCurrency(selectedCheck.financial_data.net_income)}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">總資產</p>
              <p className="text-lg font-semibold">{formatCurrency(selectedCheck.financial_data.total_assets)}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">總負債</p>
              <p className="text-lg font-semibold">{formatCurrency(selectedCheck.financial_data.total_liabilities)}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">流動比率</p>
              <p className="text-lg font-semibold">{selectedCheck.financial_data.current_ratio}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">負債權益比</p>
              <p className="text-lg font-semibold">{selectedCheck.financial_data.debt_to_equity}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">速動比率</p>
              <p className="text-lg font-semibold">{selectedCheck.financial_data.quick_ratio}</p>
            </div>
          </div>
        </div>

        {/* 風險因素 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">風險因素分析</h3>
          <div className="space-y-4">
            {selectedCheck.risk_factors.map((factor, index) => (
              <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${
                    factor.level === 'low' ? 'bg-green-100' :
                    factor.level === 'medium' ? 'bg-yellow-100' : 'bg-red-100'
                  }`}>
                    {factor.level === 'low' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : factor.level === 'medium' ? (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">{factor.factor}</h4>
                    <p className="text-sm text-gray-600">{factor.description}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(factor.level)}`}>
                  {getRiskLabel(factor.level)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 建議事項 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">建議事項</h3>
          <ul className="space-y-2">
            {selectedCheck.recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                <span className="text-gray-700">{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增信用檢查</h2>
              <p className="text-gray-600">信用檢查新增功能開發中...</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回儀表板</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default CreditCheck; 